import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import ActionBar, { actionTypeEnum } from './actionBar/ActionBar';
import { AnimatePresence } from 'framer-motion';
import useWheelDirection from '@hooks/useWheelDirection';
import SeriesSec from './sideType/series/SeriesSec';
import PostingCommentSec from './sideType/comment/PostingCommentSec';
import { IpostingItem } from '@typings/posting/posting.interface';
import { IcommentItem } from '@typings/common/comment.interface';
import Sider from './Sider';
import { deletePosting } from '@store/firebase/posting/delete-posting';
import { currentUserIDState } from '@atom/setting/user';
import { getPathKey } from '@utils/getPath';
import AttatchmentFiles from '@components/dataDisplay/AttatchmentFiles';
import GuardPage from '@template/PermissionItem/GuardPage';
import { updateBookmark } from '@store/firebase/bookmark/update-bookmark';
import { ACCESS_TYPE } from '@typings/common/accessType';
import showNav from '@utils/showNav';
import { usePrevNextDocId } from '@hooks/usePrevNextDoc';
import { usePrevNextDocCursor } from '@hooks/usePrevNextDocCursor';
import usePageSet from '@hooks/usePageSet';
import { useReadPermission } from '@hooks/usePermission';
import DetailPageHeader from '@template/BoardHeader/DetailPageHeader';
import { skipThemeCleanupOnce, useSetTheme } from '@hooks/useSetTheme';
import PostingTitleSec from '@template/PostingTitleSec/PostingTitleSec';
import BackTopBtn from '@components/dataEntry/button/BackTopBtn/BackTopBtn';
import queryString from 'query-string';
import { photocardPostingQuery } from '@typings/photocard/query';
import useToggle from '@hooks/useToggle';
import PrevNextBtn from './actionBar/PrevNextBtn';
import QuillContents from '@template/QuillContents/QuillContents';
import LoaderPage from '@template/Loading';
import PhotocardLogNavigation from './PhotocardLogNavigation';

const Container = styled.div<{
  showWide?: boolean;
  isHtmlMode?: boolean;
}>`
  position: relative;
  padding: ${({ isHtmlMode, showWide }) =>
    isHtmlMode && showWide ? '0' : '120px 0'};
  width: 100%;
  height: fit-content;
  margin: 0 auto;

  @media (max-width: 480px) {
    padding: calc(80px + env(safe-area-inset-top, 0px)) 16px 80px;
    ${({ showWide }) => showWide && wideStyle}
  }
`;

const wideStyle = css`
  padding: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 0;
    max-width: 100%;
    position: inherit; // action bar fixed 위치 재지정
  }
`;

const ActionBarWrapper = styled.div`
  position: fixed;
  top: 120px;
  left: calc(50% + 400px);
  z-index: 1;

  @media (max-width: 768px) {
    left: inherit;
    right: 32px;
    /* height: 100%; */
  }

  @media (max-width: 460px) {
  }
`;

function PostingDetail() {
  showNav(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openSide, setOpenSide] = useState(false);

  const [wheelBlock, setWheelBlock] = useState(false);
  const wheelDir = useWheelDirection({ wheelBlock: wheelBlock });
  const [sideType, setSideType] = useState('');
  const [showActionBar, , setShowActionBar] = useToggle(true);
  const handleActionBar = useCallback(() => {
    setShowActionBar((prev: boolean) => !prev);
  }, [setShowActionBar]);
  const currentUserID = useRecoilValue(currentUserIDState);
  const closeSide = useCallback(() => {
    handleActionBar();
    setOpenSide(false);
  }, [handleActionBar]);

  // -- photocard query
  const query: photocardPostingQuery = queryString.parse(location.search);
  const stringQuery = query && query.photocardId && query.photocardClosetId
    ? `?photocardId=${query.photocardId}&photocardClosetId=${query.photocardClosetId}`
    : '';
  const photocardClosetId = query.photocardClosetId || '';

  // find collectionth & path ------------------------------------
  const collectionPath = getPathKey(1);
  const prevColPath = collectionPath.replace('Posting', '');
  const isPhotocardPosting = collectionPath === 'photocardPosting';
  const [docId, setDocId] = useState<string>(getPathKey(2));
  const [isBookmark, setIsBookmark] = useState<boolean>(false);

  // docId가 없으면 목록 URL에서 렌더링된 것이므로 구독하지 않는다
  const { data: postingData, error } = useDocument<IpostingItem>(
    docId ? `${collectionPath}/${docId}` : null,
    { listen: true },
  );

  // prev, next document -----------------------------------------
  // spec: /spec/prd/posting_prev_next_cursor_prd.md
  // 시리즈 모드는 seriesData.postingList 기반, 일반 모드는 Firestore 커서 쿼리 기반.
  const { data: seriesData } = useDocument<any>(
    `${collectionPath}Series/${postingData?.series || ''}`,
    { listen: true },
  );

  const isSeriesMode = !!postingData?.series;

  const { orderBy: pageOrderBy, orderDir: pageOrderDir } = usePageSet({
    collectionPath,
  });

  const seriesRes = usePrevNextDocId({
    seriesIdList: isSeriesMode ? seriesData?.postingList : null,
    currentDocId: docId,
  });

  const cursorRes = usePrevNextDocCursor({
    collectionPath,
    currentDoc: postingData,
    orderBy: pageOrderBy,
    orderDir: pageOrderDir,
    enabled: !isSeriesMode,
  });

  const prevID = isSeriesMode ? seriesRes.prevID : cursorRes.prevID;
  const nextID = isSeriesMode ? seriesRes.nextID : cursorRes.nextID;
  const handlePrev = useCallback(() => {
    if (!prevID) {
      // ✅ 시리즈 여부에 따라 다른 메시지
      const message = postingData?.series
        ? '이전 시리즈가 없습니다.'
        : '이전 게시글이 없습니다.';
      alert(message);
      return;
    }
    // null이 아님을 확인했으므로 타입 어서션 사용
    navigate(prevID as string);
  }, [prevID, docId, postingData?.series]);

  const handleNext = useCallback(() => {
    if (!nextID) {
      // ✅ 시리즈 여부에 따라 다른 메시지
      const message = postingData?.series
        ? '다음 시리즈가 없습니다.'
        : '다음 게시글이 없습니다.';
      alert(message);
      return;
    }
    // null이 아님을 확인했으므로 타입 어서션 사용
    navigate(nextID as string);
  }, [nextID, docId, postingData?.series]);
  // prev, next document end -----------------------------------------

  // comment -------------------------------------------
  const { data: commentData } = useCollection<IcommentItem>(
    `${collectionPath}Comment`,
    { listen: true, where: ['boardId', '==', docId] },
  );

  // sider click action
  const handleClickActionBarBtn = useCallback(
    async (actionType) => {
      if (actionType === actionTypeEnum.BOOKMARK) {
        const isBookmarkExist = await updateBookmark({
          collectionPath,
          userUid: currentUserID!,
          docId,
        });
        setIsBookmark(isBookmarkExist);
      } else {
        setSideType(actionType);
        setOpenSide(true);
      }
    },
    [docId, collectionPath, currentUserID],
  );

  const handleEdit = useCallback(() => {
    if (docId) {
      skipThemeCleanupOnce();
      navigate(
        `/${collectionPath}-edit/${docId}${photocardClosetId && stringQuery}`,
      );
    }
  }, [docId, collectionPath, photocardClosetId]);

  const handleDelete = useCallback(async () => {
    if (confirm('포스팅을 삭제하시겠습니까?')) {
      await deletePosting({
        docId,
        seriesId: postingData?.series,
        hashtagIdList: postingData!.hashtagIdList,
        collectionPath: collectionPath,
        imgURLList: postingData!.imgURLList,
      });
      navigate(`/${collectionPath}`);
    } else {
      return;
    }
  }, [postingData, collectionPath, docId]);

  const scrollTop = () => {
    document.querySelector('body')!.scrollTo({ top: 0, behavior: 'smooth' });
    const editorEl = document.getElementsByClassName('parent-scroll')[0];
    if (editorEl) {
      editorEl.scrollTop = 0;
    }
  };

  // gardPage ----------------------------------
  const [isPassowrdMatch, setIsPassowrdMatch] = useState(false);
  useEffect(() => {
    setIsPassowrdMatch(false);
  }, [docId]);

  const readPermission = useReadPermission({
    accessType: postingData?.accessType || ACCESS_TYPE.PUBLIC,
    authorUid: postingData?.authorUid || '',
    isPassowrdMatch,
  });

  const SideMap = {
    [actionTypeEnum.COMMENT]: (
      <PostingCommentSec
        docId={docId}
        authorUid={postingData?.authorUid}
        showComment={postingData?.showComment}
        close={closeSide}
        commentCount={commentData ? commentData.length : 0}
      />
    ),
    [actionTypeEnum.SERIES]: (
      <SeriesSec
        seriesId={postingData?.series || ''}
        docId={docId}
        setDocId={setDocId}
        close={closeSide}
      />
    ),
  };

  useEffect(() => {
    document.addEventListener('click', handleActionBar);

    return () => {
      document.removeEventListener('click', handleActionBar);
    };
  }, [handleActionBar]);

  useEffect(() => {
    if (wheelDir === -1) setShowActionBar(true);
  }, [wheelDir]);

  useLayoutEffect(() => {
    setDocId(getPathKey(2));
    scrollTop();
    setShowActionBar(true);
  }, [location]);

  // theme 설정 ---------------------------------
  // 🎵 BGM이 있으면 커스텀 테마로 강제 인식
  const bgmURL = postingData?.themeSet?.bgmURL || '';
  const themeSetData = useMemo(() => {
    const base = postingData?.themeSet || null;
    if (!base) return null;
    if (base.bgmURL) return { ...base, isCustomTheme: true };
    return base;
  }, [
    postingData?.themeSet?.bgmURL,
    postingData?.themeSet?.backgroundColor,
    postingData?.themeSet?.backgroundImgURL,
    postingData?.themeSet?.textColor,
    postingData?.themeSet?.subTextColor,
    postingData?.themeSet?.lineColor,
    postingData?.themeSet?.cardBackgroundColor,
    postingData?.themeSet?.primaryColor,
    postingData?.themeSet?.subColor,
    postingData?.themeSet?.fontFamily,
    postingData?.themeSet?.isBgColorActive,
    postingData?.themeSet?.isBgEffectActive,
    postingData?.themeSet?.isCustomTheme,
  ]);

  useSetTheme({
    data: themeSetData,
    setMaster: true,
    defaultBgmURL: bgmURL,
    themeGroup: `world:${collectionPath.replace(/\d+$/, '')}`,
    cacheBootstrapEnabled: !postingData,
    docPath: `${collectionPath}/${docId}`,
  });
  // -------------------------------------------

  // bookmark
  useEffect(() => {
    setIsBookmark(false);
    if (postingData && postingData.bookmarkUser && currentUserID) {
      setIsBookmark(postingData.bookmarkUser.includes(currentUserID));
    }
  }, [location, postingData, currentUserID]);

  // docId가 유효하지 않으면 목록 페이지로 리다이렉트 (MainLogo 뒤로가기 시 방어)
  if (!docId) return <Navigate to={`/${collectionPath}`} replace />;

  if (!postingData || !postingData?.exists) return <LoaderPage />;

  if (!readPermission)
    return (
      <GuardPage
        accessType={postingData.accessType}
        password={postingData.password!}
        hint={postingData.passwordHint!}
        setIsPassowrdMatch={setIsPassowrdMatch}
      />
    );
  // -------------------------------------------

  return (
    <>
      <Container
        showWide={postingData.showWide}
        isHtmlMode={postingData.isHtmlMode}
        style={{ maxWidth: postingData.showWide ? '100%' : '720px' }}
      >
        <>
          <DetailPageHeader
            wheelDir={wheelDir}
            collectionPath={collectionPath}
            showWide={postingData.showWide || false}
            showActionBar={showActionBar}
            onLogoClick={
              isPhotocardPosting && query.photocardId
                ? () => {
                    const params = new URLSearchParams();
                    if (query.photocardClosetId) params.set('closetId', query.photocardClosetId);
                    params.set('tab', 'log');
                    navigate(`/${prevColPath}/${query.photocardId}?${params.toString()}`);
                  }
                : undefined
            }
          />
          <Wrapper>
            {!openSide && !isPhotocardPosting && (
              <ActionBarWrapper>
                <ActionBar
                  showWide={postingData.showWide || false}
                  showComment={postingData.showComment}
                  commentCount={commentData ? commentData.length : 0}
                  onClick={handleClickActionBarBtn}
                  isBookmark={isBookmark}
                  tooltipDir="left"
                  goPrev={handlePrev}
                  goNext={handleNext}
                  showActionBar={showActionBar}
                />
              </ActionBarWrapper>
            )}
            {!isPhotocardPosting && (
              <AnimatePresence custom={wheelDir}>
                {openSide && (
                  <Sider
                    setWheelBlock={setWheelBlock}
                    closeSide={closeSide}
                    sideType={sideType}
                    SideMap={SideMap}
                    handleClickActionBarBtn={handleClickActionBarBtn}
                    wheelDir={wheelDir}
                    commentCount={commentData ? commentData.length : 0}
                    isBookmark={isBookmark}
                    showComment={postingData.showComment}
                  />
                )}
              </AnimatePresence>
            )}
            <PostingTitleSec
              data={postingData}
              collectionPath={collectionPath}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              showTitle={postingData.showTitle || false}
              showBackBtn={postingData.showBackBtn || false}
              showWide={postingData.showWide || false}
              showActionBar={showActionBar}
              showMusic={!!postingData.themeSet?.bgmURL}
              handleBack={() => {
                if (isPhotocardPosting) {
                  const params = new URLSearchParams();
                  if (query.photocardClosetId) params.set('closetId', query.photocardClosetId);
                  params.set('tab', 'log');
                  navigate(`/${prevColPath}/${query.photocardId}?${params.toString()}`);
                } else {
                  const savedOffset = sessionStorage.getItem(`offset:${collectionPath}`);
                  const offsetParam = savedOffset && savedOffset !== '0' ? `?offset=${savedOffset}` : '';
                  navigate(`/${collectionPath}${offsetParam}`);
                }
              }}
            />

            <AttatchmentFiles fileList={postingData.attatchmentFiles} />

            <QuillContents
              contentKey={docId}
              htmlValue={postingData.htmlContents}
              isHtmlMode={postingData.isHtmlMode || false}
              value={postingData.contents}
              blockRightMouse={postingData.blockRightMouse}
            />

            {isPhotocardPosting ? (
              <PhotocardLogNavigation
                collectionPath={collectionPath}
                currentDocId={docId}
                photocardId={query.photocardId || ''}
                photocardClosetId={query.photocardClosetId || ''}
              />
            ) : !postingData.showWide ? (
              <PrevNextBtn
                collectionPath={collectionPath}
                prevID={prevID}
                nextID={nextID}
                handlePrev={handlePrev}
                handleNext={handleNext}
              />
            ) : null}
          </Wrapper>
        </>
      </Container>

      <BackTopBtn show={showActionBar} />
    </>
  );
}
export default React.memo(PostingDetail);
